/**
 * ADR-0240 Agency Portal v2 Slice 7 — smart-import preview.
 *
 * Spec §5.3:
 *   - After paste or file pick, show a preview of parsed rows:
 *     N will scan · N already-scanned (opt-in re-scan) · N skipped
 *     (with per-reason breakdown).
 *   - Primary button states the exact count: "Scan {n} domains".
 *
 * This component is a pure UI shell; the parsing + check-existing
 * calls live in the parent `AgencyReports` view (the hook is
 * composed there with the HTTP client).
 */

import { useMemo } from 'react';

import type { CheckExistingResultRow } from '../../api/agency-reports.js';
import { Button, InlineNotice } from '../../components/atoms/index.js';
import type {
  ParsedProspectRow,
  SkippedProspectRow,
  ImportRowReason,
} from '../../lib/prospect-import.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface ImportColumnUse {
  readonly field: 'domain' | 'company' | 'contact_name' | 'email';
  readonly header: string | null;
}

const FIELD_LABEL: Record<ImportColumnUse['field'], string> = {
  domain: 'Domain',
  company: 'Company',
  contact_name: 'Contact',
  email: 'Email',
};

/** Relative column widths in the sample table (fixed layout, so long
 *  values end in an ellipsis instead of widening the dialog). */
const SAMPLE_WIDTH: Record<ImportColumnUse['field'], number> = {
  domain: 32,
  company: 22,
  contact_name: 18,
  email: 28,
};

/** Rows shown in the preview sample table. */
const SAMPLE_ROWS = 5;

export interface ImportPreviewProps {
  readonly rows: readonly ParsedProspectRow[];
  readonly skipped: readonly SkippedProspectRow[];
  readonly existing: readonly CheckExistingResultRow[];
  readonly rescan_recent: boolean;
  readonly remaining_budget: number | null;
  readonly submitting: boolean;
  readonly file_label: string | null;
  /** Which source columns fed each field (header text when the file
   *  had a header row). Empty / omitted → the line is not shown. */
  readonly columns?: readonly ImportColumnUse[];
  readonly onToggleRescan: (next: boolean) => void;
  readonly onSubmit: () => void;
  readonly onCancel: () => void;
}

const REASON_COPY: Record<ImportRowReason, string> = {
  duplicate_in_file: 'duplicates in this file',
  invalid_domain: 'not valid domains',
  free_email_domain: 'personal email with no website',
  empty: 'empty rows',
  scanned_recently: 'scanned recently',
  over_batch_limit: 'over the 50-per-import limit',
  over_daily_limit: 'over today’s scan limit',
};

export function ImportPreview({
  rows,
  skipped,
  existing,
  rescan_recent,
  remaining_budget,
  submitting,
  file_label,
  columns = [],
  onToggleRescan,
  onSubmit,
  onCancel,
}: ImportPreviewProps): JSX.Element {
  const C = useC();

  const existing_set = useMemo(
    () => new Set(existing.map((e) => e.domain.toLowerCase())),
    [existing],
  );

  const rows_scanned_recently = useMemo(
    () => rows.filter((r) => existing_set.has(r.domain)),
    [rows, existing_set],
  );

  const will_scan = useMemo(() => {
    if (rescan_recent) return rows;
    return rows.filter((r) => !existing_set.has(r.domain));
  }, [rows, rescan_recent, existing_set]);

  const over_budget =
    remaining_budget !== null && will_scan.length > remaining_budget;

  const skipped_by_reason = useMemo(() => {
    const m = new Map<ImportRowReason, number>();
    for (const s of skipped) m.set(s.reason, (m.get(s.reason) ?? 0) + 1);
    return m;
  }, [skipped]);

  return (
    <div
      data-testid="import-preview"
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        background: C.surface,
        padding: 16,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.muted,
          marginBottom: 10,
        }}
      >
        {file_label ?? 'Pasted input'} · {rows.length + skipped.length} rows read
      </div>

      {columns.length > 0 ? (
        <div
          data-testid="import-preview-columns"
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            marginBottom: 10,
            lineHeight: 1.5,
          }}
        >
          Columns used:{' '}
          {columns.map((col, i) => (
            <span key={col.field}>
              {i > 0 ? ' · ' : ''}
              <span style={{ color: C.text }}>
                {col.header !== null && col.header.toLowerCase() !== FIELD_LABEL[col.field].toLowerCase()
                  ? `${col.header} → ${FIELD_LABEL[col.field]}`
                  : FIELD_LABEL[col.field]}
              </span>
            </span>
          ))}
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div style={{ overflowX: 'auto', marginBottom: 12 }}>
          <table
            data-testid="import-preview-sample"
            style={{
              width: '100%',
              tableLayout: 'fixed',
              borderCollapse: 'collapse',
              fontFamily: sansFont,
              fontSize: 12,
              color: C.text,
            }}
          >
            <thead>
              <tr>
                {(['domain', 'company', 'contact_name', 'email'] as const)
                  .filter((f) => f === 'domain' || columns.some((c) => c.field === f))
                  .map((f) => (
                    <th
                      key={f}
                      style={{
                        width: `${SAMPLE_WIDTH[f]}%`,
                        textAlign: 'left',
                        padding: '4px 8px 4px 0',
                        fontFamily: monoFont,
                        fontSize: 10,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: C.muted,
                        fontWeight: 500,
                        borderBottom: `1px solid ${C.borderSoft}`,
                      }}
                    >
                      {FIELD_LABEL[f]}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, SAMPLE_ROWS).map((r) => (
                <tr key={r.row_index}>
                  {(['domain', 'company', 'contact_name', 'email'] as const)
                    .filter((f) => f === 'domain' || columns.some((c) => c.field === f))
                    .map((f) => (
                      <td
                        key={f}
                        title={r[f] ?? undefined} /* title-ok: full value of the ellipsised cell */
                        style={{
                          padding: '4px 8px 4px 0',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontFamily: f === 'domain' || f === 'email' ? monoFont : sansFont,
                          color: r[f] === null ? C.dim : C.text,
                          borderBottom: `1px solid ${C.borderSoft}`,
                        }}
                      >
                        {r[f] ?? '—'}
                      </td>
                    ))}
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > SAMPLE_ROWS ? (
            <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, marginTop: 4 }}>
              and {rows.length - SAMPLE_ROWS} more
            </div>
          ) : null}
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div
          data-testid="import-preview-will"
          style={{
            fontFamily: sansFont,
            fontSize: 14,
            color: C.text,
            lineHeight: 1.5,
          }}
        >
          <strong style={{ fontFamily: monoFont, fontSize: 16 }}>
            {will_scan.length}
          </strong>{' '}
          will scan
        </div>
        {rows_scanned_recently.length > 0 ? (
          <label
            data-testid="import-preview-rescan"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontFamily: sansFont,
              fontSize: 13,
              color: C.muted,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={rescan_recent}
              onChange={(e) => onToggleRescan(e.target.checked)}
            />
            <span>
              Re-scan {rows_scanned_recently.length}{' '}
              {rows_scanned_recently.length === 1 ? 'domain' : 'domains'} scanned in the last 7 days
            </span>
          </label>
        ) : null}
        {skipped.length > 0 ? (
          <div data-testid="import-preview-skipped">
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.muted,
                marginBottom: 4,
              }}
            >
              <strong style={{ color: C.text, fontFamily: monoFont, fontSize: 14 }}>
                {skipped.length}
              </strong>{' '}
              skipped
            </div>
            <ul
              style={{
                paddingLeft: 20,
                margin: 0,
                fontFamily: sansFont,
                fontSize: 12,
                color: C.muted,
                lineHeight: 1.6,
              }}
            >
              {Array.from(skipped_by_reason.entries()).map(([reason, n]) => (
                <li key={reason}>
                  {n} {REASON_COPY[reason]}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      {over_budget ? (
        <div style={{ marginTop: 12 }}>
          <InlineNotice tone="warn">
            Only {remaining_budget} scans left today. The first {remaining_budget} rows will
            scan; the rest will land as "Over daily limit" and can be retried tomorrow.
          </InlineNotice>
        </div>
      ) : null}

      {remaining_budget !== null ? (
        <div
          style={{
            marginTop: 12,
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
          }}
        >
          Uses {Math.min(will_scan.length, remaining_budget)} of your {remaining_budget} scans
          left today.
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          gap: 10,
          marginTop: 16,
          justifyContent: 'flex-end',
        }}
      >
        <Button variant="secondary" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onSubmit}
          disabled={will_scan.length === 0}
          loading={submitting}
          data-testid="import-preview-submit"
        >
          {will_scan.length === 1 ? 'Scan 1 domain' : `Scan ${will_scan.length} domains`}
        </Button>
      </div>
    </div>
  );
}
